import { forwardRef, useEffect } from 'react'
import type * as React from 'react'
import type { JSX } from 'react'

import { clsx } from 'clsx'

import {
  Heading,
  IconButton,
  Text,
  UnstyledButton,
  XIcon,
} from '@buffer-mono/popcorn'

import styles from './CalendarUndatedDraftsPanel.module.css'

type CalendarUndatedDraftsPanelProps =
  React.ComponentPropsWithoutRef<'aside'> & {
    id: string
    closeButtonRef?: React.Ref<HTMLButtonElement>
    closeLabel?: string
    description?: string
    heading?: string
    isEmpty?: boolean
    isDropTarget?: boolean
    onClose: () => void
  }

export const CalendarUndatedDraftsPanel = forwardRef<
  HTMLElement,
  CalendarUndatedDraftsPanelProps
>(
  (
    {
      id,
      closeButtonRef,
      closeLabel = 'Close Undated Drafts',
      description = 'Drafts and approvals without a scheduled date.',
      heading = 'Undated drafts',
      isEmpty = false,
      isDropTarget = false,
      onClose,
      className,
      children,
      ...props
    },
    forwardedRef,
  ): JSX.Element => (
    <aside
      ref={forwardedRef}
      id={id}
      className={clsx(styles.panel, className, {
        [styles.dropTarget]: isDropTarget,
      })}
      aria-labelledby={`${id}-heading`}
      aria-describedby={`${id}-description`}
      {...props}
    >
      <div
        className={clsx(styles.header, {
          [styles.headerEmpty]: isEmpty,
        })}
      >
        {!isEmpty && (
          <>
            <Heading
              className={styles.heading}
              size="xs"
              as="h2"
              id={`${id}-heading`}
            >
              {heading}
            </Heading>
            <Text
              className={styles.description}
              as="p"
              size="sm"
              color="subtle"
              id={`${id}-description`}
            >
              {description}
            </Text>
          </>
        )}
        <IconButton
          ref={closeButtonRef}
          className={styles.close}
          variant="tertiary"
          size="medium"
          label={closeLabel}
          onClick={onClose}
        >
          <XIcon />
        </IconButton>
      </div>
      <div className={styles.body}>{children}</div>
    </aside>
  ),
)

CalendarUndatedDraftsPanel.displayName = 'CalendarUndatedDraftsPanel'

export function useUndatedDraftsPanelKeyboard({
  isOpen,
  panelRef,
  closeButtonRef,
  onClose,
  focusOnOpen = true,
  closeOnPageEscape = false,
}: {
  isOpen: boolean
  panelRef: React.RefObject<HTMLElement | null>
  closeButtonRef: React.RefObject<HTMLButtonElement>
  onClose: () => void
  focusOnOpen?: boolean
  closeOnPageEscape?: boolean
}): void {
  useEffect(() => {
    if (isOpen && focusOnOpen) {
      closeButtonRef.current?.focus()
    }
  }, [closeButtonRef, focusOnOpen, isOpen])

  useEffect(() => {
    const handleDocumentKeyDown = (event: KeyboardEvent): void => {
      if (
        event.key === 'Escape' &&
        // CalendarPostPopover's Radix DismissableLayer handles Escape in
        // the capture phase and prevents default when it closes the preview.
        // This bubble-phase sidebar handler then only runs for the next
        // Escape, preserving preview-before-sidebar close ordering.
        !event.defaultPrevented &&
        (panelRef.current?.contains(document.activeElement) ||
          (closeOnPageEscape && document.activeElement === document.body))
      ) {
        // Consume the Escape: stopping propagation at the document keeps
        // window-level handlers (useEscapeKey consumers) from also firing.
        event.stopPropagation()
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleDocumentKeyDown)
    }

    return (): void => {
      document.removeEventListener('keydown', handleDocumentKeyDown)
    }
  }, [closeOnPageEscape, isOpen, onClose, panelRef])
}

export const CalendarUndatedDraftsList = forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<'div'>
>(
  ({ className, ...props }, forwardedRef): JSX.Element => (
    <div
      ref={forwardedRef}
      className={clsx(styles.list, className)}
      {...props}
    />
  ),
)

CalendarUndatedDraftsList.displayName = 'CalendarUndatedDraftsList'

export const CalendarUndatedDraftMiniCard = forwardRef<
  HTMLElement,
  React.ComponentPropsWithoutRef<'article'>
>(
  ({ className, ...props }, forwardedRef): JSX.Element => (
    <article
      ref={forwardedRef}
      className={clsx(styles.miniCard, className)}
      {...props}
    />
  ),
)

CalendarUndatedDraftMiniCard.displayName = 'CalendarUndatedDraftMiniCard'

export const CalendarUndatedDraftMiniTrigger = forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof UnstyledButton>
>(
  ({ className, ...props }, forwardedRef): JSX.Element => (
    <UnstyledButton
      ref={forwardedRef}
      className={clsx(styles.miniTrigger, className)}
      {...props}
    />
  ),
)

CalendarUndatedDraftMiniTrigger.displayName = 'CalendarUndatedDraftMiniTrigger'
